import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import api from "../../../api/api";
import Swal from "sweetalert2";

import {
  Row,
  Col,
  Card,
  Input,
  Typography,
  Spin,
  Empty,
  Modal,
  Badge,
  Divider,
  Tag,
  Select,
} from "antd";

import {
  SearchOutlined,
  ArrowRightOutlined,
  PlusOutlined,
  MinusOutlined,
  DeleteOutlined,
  CreditCardOutlined,
  DollarOutlined,
  LockOutlined,
  QrcodeOutlined,
  ShoppingCartOutlined,
  ScanOutlined,
  CloseOutlined,
  ReloadOutlined,
  CameraOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  SyncOutlined,
} from "@ant-design/icons";

import { BrowserQRCodeReader } from "@zxing/browser";

import { useYear } from "../../../api/context/YearContext";

import PurchaseReceipt from "./PurchaseReceipt";
import RoyaltyCardPayment from "./RoyaltyPurchase/RoyaltyCardPayment";

import SUCCESS_AUDIO from "./audio/sucessess.mp3";
import ERROR_AUDIO from "./audio/erroror.mp3";

const { Title, Text } = Typography;

const Purchase = () => {
  const { selectedYear } = useYear();

  // =========================================================
  // PRODUCTS
  // =========================================================

  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);

  const [selectedProduct, setSelectedProduct] = useState(null);

  // =========================================================
  // CART
  // =========================================================

  const [purchaseItems, setPurchaseItems] = useState([]);

  const [search, setSearch] = useState("");
  const [quantity, setQuantity] = useState("");

  const [loading, setLoading] = useState(false);

  // =========================================================
  // PAYMENT
  // =========================================================

  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("");

  // =========================================================
  // CARD PAYMENT
  // =========================================================

  const [pin, setPin] = useState("");
  const [cardSubmitting, setCardSubmitting] = useState(false);

  // =========================================================
  // RECEIPT
  // =========================================================

  const [receiptOpen, setReceiptOpen] = useState(false);
  const [receiptData, setReceiptData] = useState([]);
  const [receiptTotal, setReceiptTotal] = useState(0);

  // =========================================================
  // ROYALTY
  // =========================================================

  const [royaltyOpen, setRoyaltyOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // =========================================================
  // QR SCANNER
  // =========================================================

  const [scannerOpen, setScannerOpen] = useState(false);
  const [scannerLoading, setScannerLoading] = useState(false);
  const [scannerError, setScannerError] = useState("");

  const [cameraDevices, setCameraDevices] = useState([]);
  const [selectedCameraId, setSelectedCameraId] = useState("");
  const [cameraSwitching, setCameraSwitching] = useState(false);

  const [scanStatus, setScanStatus] = useState("ready");
  const [lastScannedProduct, setLastScannedProduct] = useState(null);

  const videoRef = useRef(null);
  const scannerControlsRef = useRef(null);
  const scannerReaderRef = useRef(null);

  const mountedRef = useRef(true);

  const processingScanRef = useRef(false);
  const lastScannedTokenRef = useRef("");
  const lastScanTimeRef = useRef(0);

  const successAudioRef = useRef(null);
  const errorAudioRef = useRef(null);

  // Prevent duplicate callback requests while the same QR
  // remains directly in front of the camera.
  const duplicateDelay = 1100;

  // =========================================================
  // AUDIO
  // =========================================================

  useEffect(() => {
    successAudioRef.current = new Audio(SUCCESS_AUDIO);
    errorAudioRef.current = new Audio(ERROR_AUDIO);

    successAudioRef.current.preload = "auto";
    errorAudioRef.current.preload = "auto";

    return () => {
      try {
        successAudioRef.current?.pause();
        errorAudioRef.current?.pause();
      } catch {
        // Ignore cleanup errors.
      }

      successAudioRef.current = null;
      errorAudioRef.current = null;
    };
  }, []);

  const playSuccessSound = useCallback(() => {
    try {
      const audio = successAudioRef.current;

      if (!audio) return;

      audio.currentTime = 0;

      const promise = audio.play();

      if (promise?.catch) {
        promise.catch(() => {});
      }
    } catch {
      // Browser may block audio.
    }
  }, []);

  const playErrorSound = useCallback(() => {
    try {
      const audio = errorAudioRef.current;

      if (!audio) return;

      audio.currentTime = 0;

      const promise = audio.play();

      if (promise?.catch) {
        promise.catch(() => {});
      }
    } catch {
      // Browser may block audio.
    }
  }, []);

  // =========================================================
  // FORMAT
  // =========================================================

  const formatNumber = (num) =>
    Number(num || 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    const getAvailableUnits = useCallback((product) => {
  if (!product) {
    return 0;
  }

  if (product.available_units !== undefined && product.available_units !== null) {
    return Math.max(0, Number(product.available_units));
  }

  if (product.unit_type === "carton") {
    return Math.max(0, Number(product.units_per_carton || 0));
  }

  return Math.max(0, Number(product.quantity || 0));
}, []);

  // =========================================================
  // FETCH PRODUCTS
  // =========================================================

const fetchProducts = useCallback(async () => {
  try {
    setLoading(true);

    const response = await api.get("/api/products/", {
      params: {
        year: selectedYear?.id,
        page_size: 100,
      },
    });

    const data =
      response.data?.results ||
      response.data ||
      [];

    const normalizedProducts = data.map((product) => ({
      ...product,

      available_units:
        product.available_units !== undefined &&
        product.available_units !== null
          ? Number(product.available_units)
          : product.unit_type === "carton"
          ? Number(product.units_per_carton || 0)
          : Number(product.quantity || 0),
    }));

    setProducts(normalizedProducts);
    setFilteredProducts(normalizedProducts);
  } catch (error) {
    console.error(
      "PRODUCT FETCH ERROR:",
      error
    );

    Swal.fire({
      icon: "error",
      title: "Products Failed",
      text: "Unable to load products.",
    });
  } finally {
    if (mountedRef.current) {
      setLoading(false);
    }
  }
}, [selectedYear]);

useEffect(() => {
  if (!selectedYear) return;

  fetchProducts();
}, [selectedYear, fetchProducts]);
  

  // =========================================================
  // SEARCH
  // =========================================================

const handleSearch = (event) => {
  const value = event.target.value;

  setSearch(value);

  const normalized = value.toLowerCase().trim();

  if (!normalized) {
    setFilteredProducts(products);
    return;
  }

  setFilteredProducts(
    products.filter((product) =>
      product.name?.toLowerCase().includes(normalized)
    )
  );
};

  // =========================================================
  // PRODUCT SELECTION
  // =========================================================

  const handleSelectProduct = (product) => {
    setSelectedProduct(product);
    setQuantity("");
  };

  // =========================================================
  // KEYPAD
  // =========================================================

  const handleDigitClick = (digit) => {
    setQuantity((previous) => {
      if (previous === "0") {
        return digit;
      }

      if (previous.length >= 5) {
        return previous;
      }

      return previous + digit;
    });
  };

  const handleClear = () => {
    setQuantity("");
  };

  // =========================================================
  // ADD PRODUCT TO CART
  // =========================================================

// =========================================================
// ADD PRODUCT TO CART
// CUSTOMER BUYS INDIVIDUAL UNITS
// CARTON IS ONLY FOR STOCK/TRACKING
// =========================================================


const addProductToCart = useCallback(
  (product, requestedQuantity = 1) => {
    if (!product?.id) {
      playErrorSound();
      return false;
    }

    const qty = Number(requestedQuantity);

    if (!Number.isInteger(qty) || qty <= 0) {
      playErrorSound();

      Swal.fire({
        icon: "warning",
        title: "Invalid Quantity",
        text: "Quantity must be greater than zero.",
      });

      return false;
    }

    const availableStock = getAvailableUnits(product);

    if (availableStock <= 0) {
      playErrorSound();

      Swal.fire({
        icon: "warning",
        title: "Out of Stock",
        text: `${product.name} is currently out of stock.`,
      });

      return false;
    }

    const existingItem = purchaseItems.find(
      (item) =>
        Number(item.product) === Number(product.id)
    );

    const currentCartQuantity = existingItem
      ? Number(existingItem.quantity_taken || 0)
      : 0;

    const nextQuantity = currentCartQuantity + qty;

    if (nextQuantity > availableStock) {
      playErrorSound();

      Swal.fire({
        icon: "warning",
        title: "Insufficient Stock",
        text: `${product.name} only has ${availableStock} individual unit(s) available.`,
      });

      return false;
    }

    setPurchaseItems((previousItems) => {
      const existing = previousItems.find(
        (item) =>
          Number(item.product) === Number(product.id)
      );

      if (existing) {
        const nextQuantity =
          Number(existing.quantity_taken || 0) + qty;

        return previousItems.map((item) =>
          Number(item.product) === Number(product.id)
            ? {
                ...item,
                quantity_taken: nextQuantity,
                total:
                  Number(item.price || 0) *
                  nextQuantity,
                stock_available: availableStock,
              }
            : item
        );
      }

      return [
        ...previousItems,
        {
          product: product.id,
          product_name: product.name,

          price: Number(product.price || 0),

          // CUSTOMER BUYS INDIVIDUAL PIECES
          quantity_taken: qty,

          total:
            Number(product.price || 0) * qty,

          image: product.image,

          unit_type: product.unit_type,

          // BACKEND AUTHORITATIVE SELLABLE STOCK
          stock_available: availableStock,

          // For display only
          units_per_carton:
            Number(product.units_per_carton || 0),
        },
      ];
    });

    return true;
  },
  [
    getAvailableUnits,
    playErrorSound,
    purchaseItems,
  ]
);

  // =========================================================
  // MANUAL ADD
  // =========================================================

  const handleAddItem = () => {
    if (!selectedProduct) {
      Swal.fire({
        icon: "warning",
        title: "Select Product",
        text: "Please select a product first.",
      });

      return;
    }

    if (!quantity) {
      Swal.fire({
        icon: "warning",
        title: "Enter Quantity",
        text: "Please enter the quantity.",
      });

      return;
    }

    const success = addProductToCart(
      selectedProduct,
      Number(quantity)
    );

    if (success) {
      setQuantity("");
      setSelectedProduct(null);
    }
  };

  // =========================================================
  // INCREASE CART
  // =========================================================

// =========================================================
// INCREASE CART ITEM
// CUSTOMER BUYS ONE INDIVIDUAL UNIT AT A TIME
// =========================================================



const increaseCartItem = (item) => {
  const product = products.find(
    (productItem) =>
      Number(productItem.id) === Number(item.product)
  );

  if (!product) {
    playErrorSound();
    return;
  }

  const stock = getAvailableUnits(product);

  const currentQuantity =
    Number(item.quantity_taken || 0);

  const nextQuantity =
    currentQuantity + 1;

  if (nextQuantity > stock) {
    playErrorSound();

    Swal.fire({
      icon: "warning",
      title: "Stock Limit",
      text: `${product.name} only has ${stock} individual unit(s) available.`,
    });

    return;
  }

  setPurchaseItems((previousItems) =>
    previousItems.map((cartItem) =>
      Number(cartItem.product) === Number(item.product)
        ? {
            ...cartItem,
            quantity_taken: nextQuantity,
            total:
              Number(cartItem.price || 0) *
              nextQuantity,
            stock_available: stock,
          }
        : cartItem
    )
  );
};

  // =========================================================
  // DECREASE CART
  // =========================================================

  const decreaseCartItem = (item) => {
    setPurchaseItems((previousItems) =>
      previousItems
        .map((cartItem) => {
          if (
            Number(cartItem.product) !==
            Number(item.product)
          ) {
            return cartItem;
          }

          const nextQuantity =
            Number(cartItem.quantity_taken) - 1;

          return {
            ...cartItem,
            quantity_taken: nextQuantity,
            total:
              Number(cartItem.price) * nextQuantity,
          };
        })
        .filter(
          (cartItem) =>
            Number(cartItem.quantity_taken) > 0
        )
    );
  };

  // =========================================================
  // REMOVE
  // =========================================================

  const removeCartItem = (item) => {
    setPurchaseItems((previousItems) =>
      previousItems.filter(
        (cartItem) =>
          Number(cartItem.product) !== Number(item.product)
      )
    );
  };

  // =========================================================
  // CLEAR CART
  // =========================================================

  const clearCart = async () => {
    if (!purchaseItems.length) return;

    const result = await Swal.fire({
      icon: "warning",
      title: "Clear Cart?",
      text: "All items will be removed from this transaction.",
      showCancelButton: true,
      confirmButtonText: "Yes, Clear Cart",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc2626",
    });

    if (!result.isConfirmed) return;

    setPurchaseItems([]);
    setQuantity("");
    setSelectedProduct(null);
  };

  // =========================================================
  // TOTALS
  // =========================================================

  const totalItems = useMemo(
    () =>
      purchaseItems.reduce(
        (total, item) =>
          total + Number(item.quantity_taken || 0),
        0
      ),
    [purchaseItems]
  );

  const overallTotal = useMemo(
    () =>
      purchaseItems.reduce(
        (total, item) =>
          total + Number(item.total || 0),
        0
      ),
    [purchaseItems]
  );

  // =========================================================
  // QR TOKEN EXTRACTION
  // =========================================================

  const extractQRToken = useCallback((rawValue) => {
    if (!rawValue) return null;

    const value = String(rawValue).trim();

    try {
      const parsed = JSON.parse(value);

      if (parsed?.type === "product") {
        return parsed.qr_token || null;
      }

      if (parsed?.qr_token) {
        return parsed.qr_token;
      }

      if (parsed?.token) {
        return parsed.token;
      }
    } catch {
      // QR can contain a raw UUID/token.
    }

    const uuidMatch = value.match(
      /[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/i
    );

    if (uuidMatch) {
      return uuidMatch[0];
    }

    return value;
  }, []);

  // =========================================================
  // VERIFY PRODUCT QR
  // =========================================================

  const verifyProductQR = useCallback(
    async (rawValue) => {
      const qrToken = extractQRToken(rawValue);

      if (!qrToken) {
        return;
      }

      const now = Date.now();

      /*
       * Prevent ZXing from firing the same QR
       * many times while it remains inside
       * the camera frame.
       */
      if (processingScanRef.current) {
        return;
      }

      if (
        lastScannedTokenRef.current === qrToken &&
        now - lastScanTimeRef.current < duplicateDelay
      ) {
        return;
      }

      processingScanRef.current = true;

      lastScannedTokenRef.current = qrToken;
      lastScanTimeRef.current = now;

      setScanStatus("processing");

      try {
        /*
         * IMPORTANT:
         *
         * The backend is the source of truth.
         *
         * The frontend does NOT trust:
         *
         * - QR price
         * - QR product name
         * - QR stock
         *
         * It sends only the token.
         */
        const response = await api.post(
          "/api/products/verify-qr/",
          {
            qr_token: qrToken,
          }
        );

        const product =
          response.data?.product ||
          response.data;

        if (!product?.id) {
          throw new Error(
            "The server returned an invalid product."
          );
        }

        const added = addProductToCart(product, 1);

        if (!added) {
          setScanStatus("error");
          return;
        }

        /*
         * SUCCESS
         */
        playSuccessSound();

        setLastScannedProduct(product);
        setScanStatus("success");

        /*
         * Small visual success state,
         * then scanner goes back to READY.
         */
        window.setTimeout(() => {
          if (mountedRef.current) {
            setScanStatus("ready");
          }
        }, 650);

      } catch (error) {
        console.error(
          "QR VERIFY ERROR:",
          error.response?.data || error
        );

        playErrorSound();

        setScanStatus("error");

        const backendMessage =
          error.response?.data?.error ||
          error.response?.data?.message ||
          "Unable to verify this product QR code.";

        /*
         * Automatically return scanner to READY
         * after showing the error.
         */
        Swal.fire({
          icon: "error",
          title: "Scan Failed",
          text: backendMessage,
          confirmButtonText: "OK",
          confirmButtonColor: "#1d4ed8",
        }).then(() => {
          if (mountedRef.current) {
            setScanStatus("ready");
          }
        });
      } finally {
        processingScanRef.current = false;
      }
    },
    [
      addProductToCart,
      extractQRToken,
      playErrorSound,
      playSuccessSound,
    ]
  );

  // =========================================================
  // STOP CAMERA
  // =========================================================

  const stopScanner = useCallback(() => {
    try {
      if (scannerControlsRef.current) {
        scannerControlsRef.current.stop();
        scannerControlsRef.current = null;
      }
    } catch (error) {
      console.warn("Scanner controls stop:", error);
    }

    try {
      if (scannerReaderRef.current) {
        scannerReaderRef.current.reset();
        scannerReaderRef.current = null;
      }
    } catch (error) {
      console.warn("Scanner reader reset:", error);
    }

    try {
      if (videoRef.current?.srcObject) {
        const stream = videoRef.current.srcObject;

        stream.getTracks().forEach((track) => {
          try {
            track.stop();
          } catch {
            // Ignore.
          }
        });

        videoRef.current.srcObject = null;
      }
    } catch (error) {
      console.warn("Camera stream cleanup:", error);
    }

    processingScanRef.current = false;
    lastScannedTokenRef.current = "";
    lastScanTimeRef.current = 0;

    if (mountedRef.current) {
      setScanStatus("ready");
    }
  }, []);

  // =========================================================
  // FIND BEST CAMERA
  // =========================================================

  const findPreferredCamera = useCallback(
    (devices) => {
      if (!devices?.length) {
        return null;
      }

      /*
       * If user has explicitly selected a camera,
       * always respect that choice.
       */
      if (selectedCameraId) {
        const selected = devices.find(
          (device) =>
            device.deviceId === selectedCameraId
        );

        if (selected) {
          return selected;
        }
      }

      /*
       * Prefer rear/environment camera on phones.
       */
      const rearCamera = devices.find((device) => {
        const label =
          device.label?.toLowerCase() || "";

        return (
          label.includes("back") ||
          label.includes("rear") ||
          label.includes("environment") ||
          label.includes("facing back")
        );
      });

      if (rearCamera) {
        return rearCamera;
      }

      return devices[0];
    },
    [selectedCameraId]
  );

  // =========================================================
  // LOAD CAMERA DEVICES
  // =========================================================

  const loadCameraDevices = useCallback(async () => {
    try {
      /*
       * Request camera permission first.
       *
       * This is important on Android because
       * camera labels may be empty before permission.
       */
      let temporaryStream = null;

      try {
        temporaryStream =
          await navigator.mediaDevices.getUserMedia({
            video: true,
            audio: false,
          });
      } catch (permissionError) {
        throw permissionError;
      } finally {
        if (temporaryStream) {
          temporaryStream
            .getTracks()
            .forEach((track) => track.stop());
        }
      }

      const devices =
        await BrowserQRCodeReader.listVideoInputDevices();

      const videoDevices = devices.filter(
        (device) =>
          device.kind === "videoinput" ||
          !device.kind
      );

      if (!videoDevices.length) {
        throw new Error(
          "No camera was found on this device."
        );
      }

      setCameraDevices(videoDevices);

      const preferred =
        findPreferredCamera(videoDevices);

      if (preferred) {
        setSelectedCameraId(preferred.deviceId);
      }

      return videoDevices;
    } catch (error) {
      console.error(
        "CAMERA DEVICE ERROR:",
        error
      );

      throw error;
    }
  }, [findPreferredCamera]);

  // =========================================================
  // START SCANNER WITH CAMERA
  // =========================================================

  const startScanner = useCallback(
    async (requestedCameraId = null) => {
      if (!videoRef.current) {
        return;
      }

      try {
        setScannerLoading(true);
        setScannerError("");
        setScanStatus("ready");

        stopScanner();

        let devices = cameraDevices;

        /*
         * If we don't have devices yet,
         * discover them first.
         */
        if (!devices.length) {
          devices = await loadCameraDevices();
        }

        if (!devices.length) {
          throw new Error(
            "No camera was found on this device."
          );
        }

        let cameraId =
          requestedCameraId ||
          selectedCameraId;

        /*
         * If requested ID doesn't exist anymore,
         * choose the preferred camera.
         */
        const cameraExists = devices.some(
          (device) =>
            device.deviceId === cameraId
        );

        if (!cameraExists) {
          const preferred =
            findPreferredCamera(devices);

          cameraId =
            preferred?.deviceId ||
            devices[0]?.deviceId;
        }

        if (!cameraId) {
          throw new Error(
            "Unable to select a camera."
          );
        }

        setSelectedCameraId(cameraId);

        const reader =
          new BrowserQRCodeReader();

        scannerReaderRef.current = reader;

        /*
         * ZXing continuously analyzes the video.
         *
         * There is NO scan button.
         */
        const controls =
          await reader.decodeFromVideoDevice(
            cameraId,
            videoRef.current,
            async (result, error) => {
              /*
               * NotFoundException is completely normal.
               *
               * ZXing throws it when it simply hasn't
               * detected a QR code in the current frame.
               */
              if (result) {
                const text = result.getText();

                if (text) {
                  await verifyProductQR(text);
                }
              }

              /*
               * Do not display continuous
               * NotFoundException errors.
               */
              if (
                error &&
                error.name !== "NotFoundException"
              ) {
                console.debug(
                  "ZXing scanner:",
                  error
                );
              }
            }
          );

        scannerControlsRef.current = controls;

      } catch (error) {
        console.error(
          "SCANNER START ERROR:",
          error
        );

        let message =
          "Unable to start the camera.";

        if (
          error?.name ===
          "NotAllowedError"
        ) {
          message =
            "Camera permission was denied. Please allow camera access in your browser settings.";
        } else if (
          error?.name ===
          "NotFoundError"
        ) {
          message =
            "No camera was found on this device.";
        } else if (
          error?.name ===
          "NotReadableError"
        ) {
          message =
            "The camera is already being used by another application.";
        } else if (
          error?.name ===
          "OverconstrainedError"
        ) {
          message =
            "The selected camera is not available. Please select another camera.";
        } else if (
          error?.name ===
          "SecurityError"
        ) {
          message =
            "Camera access is blocked by the browser security settings.";
        } else if (error?.message) {
          message = error.message;
        }

        setScannerError(message);
        setScanStatus("error");

        playErrorSound();

      } finally {
        if (mountedRef.current) {
          setScannerLoading(false);
          setCameraSwitching(false);
        }
      }
    },
    [
      cameraDevices,
      findPreferredCamera,
      loadCameraDevices,
      playErrorSound,
      selectedCameraId,
      stopScanner,
      verifyProductQR,
    ]
  );

  // =========================================================
  // OPEN SCANNER
  // =========================================================

  const openScanner = async () => {
    setScannerOpen(true);
    setScannerError("");
    setLastScannedProduct(null);
    setScanStatus("ready");

    /*
     * Wait until the modal/video element exists.
     */
    window.setTimeout(() => {
      if (mountedRef.current) {
        startScanner();
      }
    }, 180);
  };

  // =========================================================
  // CLOSE SCANNER
  // =========================================================

  const closeScanner = () => {
    stopScanner();

    setScannerOpen(false);
    setScannerError("");
    setLastScannedProduct(null);
    setScanStatus("ready");
  };

  // =========================================================
  // SWITCH CAMERA
  // =========================================================

  const handleCameraChange = async (cameraId) => {
    if (!cameraId) return;

    setSelectedCameraId(cameraId);
    setCameraSwitching(true);
    setScannerError("");

    /*
     * Stop current camera and immediately
     * restart ZXing using the selected camera.
     */
    await startScanner(cameraId);
  };

  // =========================================================
  // CLEANUP
  // =========================================================

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      stopScanner();
    };
  }, [stopScanner]);

  // =========================================================
  // PAYMENT
  // =========================================================

  const openPaymentModal = () => {
    if (!purchaseItems.length) {
      Swal.fire({
        icon: "warning",
        title: "Cart Empty",
        text: "Add products before submitting the purchase.",
      });

      return;
    }

    setPaymentMethod("");
    setPin("");
    setPaymentModalOpen(true);
  };

  // =========================================================
  // RESET TRANSACTION
  // =========================================================

  const resetTransaction = () => {
    setPurchaseItems([]);
    setQuantity("");
    setSelectedProduct(null);
    setPaymentMethod("");
    setPin("");
  };

  // =========================================================
  // CASH
  // =========================================================

  const handleCashPayment = async () => {
    if (!purchaseItems.length) return;

    try {
      setLoading(true);

      const response = await api.post(
        "/api/purchases/",
        {
          items: purchaseItems.map((item) => ({
            product: item.product,
            quantity_taken:
              Number(item.quantity_taken),
          })),
          payment_method: "cash",
          year: selectedYear?.id,
        }
      );

      setReceiptData(purchaseItems);
      setReceiptTotal(overallTotal);

      setPaymentModalOpen(false);
      setReceiptOpen(true);

      Swal.fire({
        icon: "success",
        title: "Sale Completed",
        text: "Cash payment successfully recorded.",
        timer: 1800,
        showConfirmButton: false,
      });

      resetTransaction();

      await fetchProducts();

      return response;
    } catch (error) {
      console.error(
        "CASH PAYMENT ERROR:",
        error.response?.data
      );

      Swal.fire({
        icon: "error",
        title: "Cash Payment Failed",
        text:
          error.response?.data?.error ||
          error.response?.data?.message ||
          "Unable to complete cash payment.",
      });
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // CARD
  // =========================================================

  const completeCardPayment = async () => {
    if (cardSubmitting) return;

    if (!pin.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Missing PIN",
        text: "Please enter the customer's card PIN.",
      });

      return;
    }

    setCardSubmitting(true);

    try {
      const response = await api.post(
        "/api/complete-card-payment/",
        {
          pin,
          items: purchaseItems.map((item) => ({
            product: item.product,
            quantity_taken:
              Number(item.quantity_taken),
          })),
          year: selectedYear?.id,
        }
      );

      setReceiptData(purchaseItems);
      setReceiptTotal(overallTotal);

      setPaymentModalOpen(false);
      setReceiptOpen(true);

      Swal.fire({
        icon: "success",
        title: "Payment Successful",
        text: "Card payment completed successfully.",
        timer: 1800,
        showConfirmButton: false,
      });

      resetTransaction();

      await fetchProducts();

      if (
        response.data?.remaining_balance !==
        undefined
      ) {
        window.setTimeout(() => {
          if (!mountedRef.current) return;

          Swal.fire({
            icon: "info",
            title: "Card Balance",
            text: `Remaining balance: $${formatNumber(
              response.data.remaining_balance
            )}`,
          });
        }, 1900);
      }
    } catch (error) {
      console.error(
        "CARD PAYMENT ERROR:",
        error.response?.data
      );

      Swal.fire({
        icon: "error",
        title: "Payment Failed",
        text:
          error.response?.data?.message ||
          error.response?.data?.error ||
          "Card payment could not be completed.",
      });
    } finally {
      setCardSubmitting(false);
    }
  };

  // =========================================================
  // ROYALTY
  // =========================================================

  const openRoyaltyPayment = () => {
    if (!purchaseItems.length) {
      Swal.fire({
        icon: "warning",
        title: "Cart Empty",
        text: "Add products before using Royalty Card Payment.",
      });

      return;
    }

    setRoyaltyOpen(true);
  };

  // =========================================================
  // DIGITS
  // =========================================================

  const digits = [
    1, 2, 3,
    4, 5, 6,
    7, 8, 9,
  ];

  // =========================================================
  // CAMERA OPTIONS
  // =========================================================

  const cameraOptions = cameraDevices.map(
    (device, index) => ({
      value: device.deviceId,
      label:
        device.label ||
        `Camera ${index + 1}`,
    })
  );

  // =========================================================
  // SCANNER STATUS
  // =========================================================

  const scannerStatus = {
    ready: {
      label: "READY TO SCAN",
      icon: <ScanOutlined />,
    },

    processing: {
      label: "VERIFYING PRODUCT...",
      icon: <SyncOutlined spin />,
    },

    success: {
      label: "PRODUCT ADDED",
      icon: <CheckCircleOutlined />,
    },

    error: {
      label: "SCAN ERROR",
      icon: <WarningOutlined />,
    },
  };

  // =========================================================
  // INLINE STYLES
  // =========================================================

  const styles = `
    * {
      box-sizing: border-box;
    }

    .pos-page {
      min-height: 100vh;
      padding: 18px;
      background:
        radial-gradient(circle at top right, rgba(37, 99, 235, 0.08), transparent 32%),
        radial-gradient(circle at bottom left, rgba(14, 165, 233, 0.07), transparent 28%),
        #f4f7fb;
      color: #172033;
    }

    .pos-header {
      min-height: 82px;
      padding: 18px 20px;
      border-radius: 20px;
      background: linear-gradient(135deg, #0f172a, #172554);
      color: white;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 20px;
      margin-bottom: 18px;
      box-shadow: 0 16px 45px rgba(15, 23, 42, 0.16);
    }

    .pos-header-left {
      display: flex;
      align-items: center;
      gap: 14px;
      min-width: 0;
    }

    .pos-header-left h3 {
      color: white !important;
    }

    .pos-header-subtitle {
      color: rgba(255,255,255,0.68);
      font-size: 13px;
    }

    .pos-icon {
      width: 48px;
      height: 48px;
      min-width: 48px;
      border-radius: 14px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 22px;
      background: linear-gradient(135deg, #2563eb, #06b6d4);
      box-shadow: 0 8px 25px rgba(37,99,235,0.35);
    }

    .pos-header-actions {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 12px;
    }

    .pos-scan-button {
      border: 0;
      border-radius: 12px;
      padding: 12px 18px;
      min-height: 46px;
      display: flex;
      align-items: center;
      gap: 9px;
      color: white;
      background: linear-gradient(135deg, #2563eb, #0891b2);
      cursor: pointer;
      font-weight: 700;
      box-shadow: 0 8px 22px rgba(37,99,235,0.3);
      transition: 0.2s ease;
    }

    .pos-scan-button:hover {
      transform: translateY(-2px);
      box-shadow: 0 12px 28px rgba(37,99,235,0.4);
    }

    .pos-total-header {
      min-width: 150px;
      padding: 9px 14px;
      border-radius: 12px;
      background: rgba(255,255,255,0.09);
      border: 1px solid rgba(255,255,255,0.1);
    }

    .pos-total-header span {
      display: block;
      font-size: 11px;
      color: rgba(255,255,255,0.6);
    }

    .pos-total-header strong {
      display: block;
      color: #67e8f9;
      font-size: 20px;
      margin-top: 2px;
    }

    .pos-panel {
      height: 100%;
      border: 0 !important;
      border-radius: 18px !important;
      overflow: hidden;
      box-shadow: 0 8px 30px rgba(15,23,42,0.07);
    }

    .pos-panel .ant-card-body {
      padding: 18px;
      height: 100%;
    }

    .panel-title {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
      margin-bottom: 15px;
    }

    .panel-title h4 {
      color: #0f172a !important;
    }

    .pos-search {
      border-radius: 12px !important;
      height: 46px;
      margin-bottom: 14px;
    }

    .product-list {
      height: 575px;
      overflow-y: auto;
      padding-right: 3px;
    }

    .product-list::-webkit-scrollbar,
    .cart-list::-webkit-scrollbar {
      width: 6px;
    }

    .product-list::-webkit-scrollbar-thumb,
    .cart-list::-webkit-scrollbar-thumb {
      background: #cbd5e1;
      border-radius: 10px;
    }

    .pos-product {
      width: 100%;
      border: 1px solid #e2e8f0;
      background: white;
      padding: 10px;
      border-radius: 14px;
      display: flex;
      align-items: center;
      gap: 11px;
      text-align: left;
      margin-bottom: 9px;
      cursor: pointer;
      transition: 0.18s ease;
    }

    .pos-product:hover,
    .pos-product.selected-product {
      border-color: #2563eb;
      background: #eff6ff;
      transform: translateY(-1px);
      box-shadow: 0 7px 18px rgba(37,99,235,0.1);
    }

    .pos-product-image-wrapper {
      width: 58px;
      height: 58px;
      min-width: 58px;
      overflow: hidden;
      border-radius: 11px;
      background: #f1f5f9;
    }

    .product-image {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .product-image-placeholder {
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #64748b;
      font-size: 20px;
    }

    .pos-product-details {
      min-width: 0;
      flex: 1;
    }

    .pos-product-details .ant-typography {
      display: block;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .price-text {
      color: #2563eb !important;
      font-weight: 800;
      margin-top: 3px;
    }

    .stock-text {
      font-size: 11px;
    }

    .cart-panel {
      display: flex;
      flex-direction: column;
    }

    .cart-list {
      height: 460px;
      overflow-y: auto;
    }

    .empty-cart {
      height: 100%;
      min-height: 380px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-direction: column;
      text-align: center;
      padding: 20px;
    }

    .empty-cart-icon {
      width: 76px;
      height: 76px;
      border-radius: 22px;
      background: #eff6ff;
      color: #2563eb;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 32px;
      margin-bottom: 15px;
    }

    .empty-scan-button {
      margin-top: 15px;
      border: 0;
      border-radius: 11px;
      padding: 11px 17px;
      color: white;
      background: #2563eb;
      font-weight: 700;
      cursor: pointer;
    }

    .cart-item {
      display: grid;
      grid-template-columns: minmax(0, 1fr) auto auto auto;
      align-items: center;
      gap: 10px;
      padding: 12px 0;
      border-bottom: 1px solid #eef2f7;
    }

    .cart-item-name {
      font-weight: 700;
      color: #0f172a;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .cart-item-price {
      color: #64748b;
      font-size: 12px;
      margin-top: 4px;
    }

    .cart-item-controls {
      display: flex;
      align-items: center;
      gap: 7px;
    }

    .cart-item-controls button,
    .remove-item-button {
      width: 30px;
      height: 30px;
      border: 0;
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
    }

    .cart-item-controls button {
      background: #eef2ff;
      color: #1d4ed8;
    }

    .cart-item-controls span {
      min-width: 25px;
      text-align: center;
      font-weight: 800;
    }

    .cart-item-total {
      font-weight: 800;
      color: #0f172a;
      white-space: nowrap;
    }

    .remove-item-button {
      color: #dc2626;
      background: #fef2f2;
    }

    .clear-cart-button {
      border: 0;
      border-radius: 9px;
      padding: 7px 10px;
      background: #fef2f2;
      color: #dc2626;
      cursor: pointer;
      font-weight: 700;
    }

    .cart-summary > div {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 9px;
    }

    .cart-summary span {
      color: #64748b;
    }

    .cart-summary strong {
      color: #0f172a;
    }

    .cart-summary .grand-total {
      margin-top: 13px;
      padding: 14px;
      border-radius: 13px;
      background: linear-gradient(135deg, #eff6ff, #ecfeff);
    }

    .cart-summary .grand-total span {
      color: #1e40af;
      font-weight: 800;
    }

    .cart-summary .grand-total strong {
      color: #1d4ed8;
      font-size: 24px;
    }

    .calculator-screen {
      height: 82px;
      border-radius: 15px;
      background: #0f172a;
      display: flex;
      align-items: center;
      justify-content: flex-end;
      padding: 15px;
      color: #67e8f9;
      overflow: hidden;
      margin-bottom: 12px;
    }

    .calculator-screen span {
      font-size: 38px;
      font-weight: 800;
    }

    .selected-product-summary {
      border-radius: 11px;
      padding: 10px 12px;
      background: #f8fafc;
      margin-bottom: 12px;
    }

    .selected-product-summary > div {
      display: flex;
      justify-content: space-between;
      gap: 8px;
    }

    .selected-product-summary span {
      color: #2563eb;
      font-weight: 800;
    }

    .selected-product-summary small {
      color: #64748b;
    }

    .digit-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 8px;
    }

    .digit-btn,
    .clear-btn,
    .add-btn {
      height: 55px;
      border: 0;
      border-radius: 11px;
      cursor: pointer;
      font-size: 19px;
      font-weight: 800;
      transition: 0.15s ease;
    }

    .digit-btn {
      background: #f1f5f9;
      color: #0f172a;
    }

    .digit-btn:hover {
      background: #e2e8f0;
    }

    .clear-btn {
      background: #fee2e2;
      color: #dc2626;
    }

    .add-btn {
      background: linear-gradient(135deg, #2563eb, #0891b2);
      color: white;
    }

    .full-submit-btn {
      width: 100%;
      min-height: 53px;
      border: 0;
      border-radius: 13px;
      margin-top: 13px;
      background: linear-gradient(135deg, #2563eb, #0891b2);
      color: white;
      font-weight: 800;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      cursor: pointer;
      box-shadow: 0 9px 24px rgba(37,99,235,0.22);
    }

    .full-submit-btn:disabled {
      opacity: 0.45;
      cursor: not-allowed;
      box-shadow: none;
    }

    .loading-box {
      height: 300px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-direction: column;
      gap: 12px;
    }

    /* =====================================================
       PAYMENT
       ===================================================== */

    .payment-modal .ant-modal-content {
      border-radius: 20px;
      overflow: hidden;
    }

    .checkout-heading {
      display: flex;
      align-items: center;
      gap: 13px;
      margin-bottom: 18px;
    }

    .checkout-icon {
      width: 48px;
      height: 48px;
      min-width: 48px;
      border-radius: 14px;
      background: #eff6ff;
      color: #2563eb;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 21px;
    }

    .checkout-total {
      padding: 17px;
      border-radius: 14px;
      background: linear-gradient(135deg, #0f172a, #172554);
      margin-bottom: 16px;
      color: white;
    }

    .checkout-total span {
      display: block;
      color: rgba(255,255,255,0.65);
      font-size: 12px;
    }

    .checkout-total strong {
      display: block;
      font-size: 29px;
      color: #67e8f9;
      margin-top: 3px;
    }

    .payment-buttons {
      display: grid;
      gap: 10px;
    }

    .cash-payment-btn,
    .card-payment-btn,
    .royalty-payment-btn {
      border: 1px solid #e2e8f0;
      background: white;
      min-height: 67px;
      border-radius: 13px;
      display: flex;
      align-items: center;
      gap: 14px;
      padding: 11px 14px;
      text-align: left;
      cursor: pointer;
      transition: 0.18s ease;
    }

    .cash-payment-btn {
      color: #15803d;
    }

    .card-payment-btn {
      color: #1d4ed8;
    }

    .royalty-payment-btn {
      color: #7c3aed;
    }

    .cash-payment-btn:hover,
    .card-payment-btn:hover,
    .royalty-payment-btn:hover,
    .active-payment {
      border-color: currentColor;
      background: #f8fafc;
      transform: translateY(-1px);
    }

    .cash-payment-btn > span,
    .card-payment-btn > span,
    .royalty-payment-btn > span {
      font-size: 22px;
    }

    .payment-buttons strong,
    .payment-buttons small {
      display: block;
    }

    .payment-buttons small {
      color: #64748b;
      margin-top: 2px;
    }

    .card-payment-section {
      margin-top: 4px;
    }

    .card-input {
      margin-top: 8px;
      border-radius: 11px !important;
    }

    .purchase-total-box {
      margin-top: 12px;
      padding: 12px 14px;
      border-radius: 11px;
      background: #f8fafc;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .purchase-total-box strong {
      color: #2563eb;
      font-size: 18px;
    }

    .complete-payment-btn {
      width: 100%;
      min-height: 50px;
      margin-top: 12px;
      border: 0;
      border-radius: 11px;
      background: #2563eb;
      color: white;
      font-weight: 800;
      cursor: pointer;
    }

    .complete-payment-btn:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    /* =====================================================
       SCANNER
       ===================================================== */

    .scanner-modal .ant-modal-content {
      padding: 0 !important;
      overflow: hidden;
      border-radius: 22px;
      background: #07111f;
    }

    .scanner-container {
      background:
        radial-gradient(circle at top, rgba(37,99,235,0.18), transparent 40%),
        #07111f;
      color: white;
      padding: 17px;
    }

    .scanner-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 12px;
      margin-bottom: 13px;
    }

    .scanner-header h3 {
      color: white !important;
    }

    .scanner-header-subtitle {
      color: rgba(255,255,255,0.58);
      font-size: 12px;
    }

    .scanner-close-button {
      width: 38px;
      height: 38px;
      border: 1px solid rgba(255,255,255,0.12);
      border-radius: 10px;
      background: rgba(255,255,255,0.06);
      color: white;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .camera-control-bar {
      display: flex;
      align-items: center;
      gap: 9px;
      margin-bottom: 12px;
    }

    .camera-label {
      color: rgba(255,255,255,0.72);
      font-size: 12px;
      white-space: nowrap;
    }

    .scanner-camera-select {
      flex: 1;
    }

    .scanner-camera-select .ant-select-selector {
      background: rgba(255,255,255,0.08) !important;
      border: 1px solid rgba(255,255,255,0.13) !important;
      color: white !important;
      border-radius: 10px !important;
      min-height: 40px !important;
    }

    .scanner-camera-select .ant-select-selection-item {
      color: white !important;
    }

    .scanner-camera-wrapper {
      position: relative;
      width: 100%;
      aspect-ratio: 4 / 3;
      max-height: 440px;
      overflow: hidden;
      border-radius: 18px;
      background: #000;
      border: 1px solid rgba(255,255,255,0.1);
    }

    .scanner-video {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    /*
     * Dark overlay around the QR target.
     */
    .scanner-camera-wrapper::after {
      content: "";
      position: absolute;
      inset: 0;
      pointer-events: none;
      background:
        linear-gradient(
          rgba(0,0,0,0.30),
          rgba(0,0,0,0.30)
        );
    }

    .scanner-frame {
      position: absolute;
      z-index: 4;
      width: min(72%, 310px);
      aspect-ratio: 1;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
      pointer-events: none;
    }

    .scanner-frame::before,
    .scanner-frame::after {
      content: "";
      position: absolute;
      pointer-events: none;
    }

    /*
     * Moving laser scanner line.
     */
    .scanner-line {
      position: absolute;
      z-index: 5;
      left: 5%;
      right: 5%;
      top: 5%;
      height: 2px;
      border-radius: 100px;
      background: linear-gradient(
        90deg,
        transparent,
        #22d3ee 15%,
        #ffffff 50%,
        #22d3ee 85%,
        transparent
      );
      box-shadow:
        0 0 6px #22d3ee,
        0 0 14px #22d3ee,
        0 0 28px rgba(34,211,238,0.8);
      animation: scannerLaser 2s ease-in-out infinite;
    }

    @keyframes scannerLaser {
      0% {
        top: 5%;
        opacity: 0.4;
      }

      50% {
        opacity: 1;
      }

      100% {
        top: 95%;
        opacity: 0.4;
      }
    }

    /*
     * QR scanning corners.
     */
    .scanner-corner {
      position: absolute;
      z-index: 6;
      width: 38px;
      height: 38px;
      border-color: #22d3ee;
      border-style: solid;
      filter: drop-shadow(0 0 5px rgba(34,211,238,0.8));
    }

    .scanner-corner.top-left {
      top: 0;
      left: 0;
      border-width: 4px 0 0 4px;
      border-radius: 7px 0 0 0;
    }

    .scanner-corner.top-right {
      top: 0;
      right: 0;
      border-width: 4px 4px 0 0;
      border-radius: 0 7px 0 0;
    }

    .scanner-corner.bottom-left {
      bottom: 0;
      left: 0;
      border-width: 0 0 4px 4px;
      border-radius: 0 0 0 7px;
    }

    .scanner-corner.bottom-right {
      bottom: 0;
      right: 0;
      border-width: 0 4px 4px 0;
      border-radius: 0 0 7px 0;
    }

    .scanner-loading {
      position: absolute;
      z-index: 10;
      inset: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-direction: column;
      gap: 10px;
      background: rgba(3,7,18,0.72);
      backdrop-filter: blur(3px);
    }

    .scanner-loading span {
      color: white;
      font-size: 13px;
      font-weight: 700;
    }

    .scanner-error-box {
      margin-top: 11px;
      border-radius: 12px;
      padding: 11px;
      background: rgba(220,38,38,0.12);
      border: 1px solid rgba(248,113,113,0.3);
      color: #fecaca;
    }

    .scanner-retry {
      border: 0;
      margin-top: 8px;
      padding: 8px 12px;
      border-radius: 8px;
      background: #2563eb;
      color: white;
      font-weight: 700;
      cursor: pointer;
    }

    .scanner-status {
      margin-top: 12px;
      min-height: 42px;
      border-radius: 11px;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      padding: 8px 12px;
      background: rgba(255,255,255,0.06);
      border: 1px solid rgba(255,255,255,0.08);
      color: #67e8f9;
      font-size: 12px;
      font-weight: 800;
      letter-spacing: 0.4px;
    }

    .scanner-status.success {
      background: rgba(22,163,74,0.14);
      color: #86efac;
    }

    .scanner-status.error {
      background: rgba(220,38,38,0.14);
      color: #fca5a5;
    }

    .last-scanned-product {
      margin-top: 9px;
      padding: 9px 11px;
      border-radius: 10px;
      background: rgba(22,163,74,0.12);
      border: 1px solid rgba(74,222,128,0.18);
      display: flex;
      align-items: center;
      gap: 9px;
    }

    .last-scanned-product-icon {
      color: #4ade80;
      font-size: 18px;
    }

    .last-scanned-product strong {
      display: block;
      color: white;
      font-size: 13px;
    }

    .last-scanned-product span {
      color: rgba(255,255,255,0.55);
      font-size: 11px;
    }

    .scanner-instructions {
      margin-top: 11px;
      display: flex;
      align-items: flex-start;
      gap: 10px;
      padding: 11px;
      border-radius: 11px;
      background: rgba(255,255,255,0.045);
    }

    .scanner-instructions > span {
      color: #22d3ee;
      font-size: 20px;
    }

    .scanner-instructions strong,
    .scanner-instructions span {
      display: block;
    }

    .scanner-instructions strong {
      font-size: 12px;
      color: white;
    }

    .scanner-instructions div span {
      color: rgba(255,255,255,0.55);
      font-size: 11px;
      line-height: 1.45;
      margin-top: 2px;
    }

    .scanner-cart-status {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 8px;
      margin-top: 10px;
    }

    .scanner-cart-status > div {
      border-radius: 10px;
      padding: 10px;
      background: rgba(255,255,255,0.055);
    }

    .scanner-cart-status span,
    .scanner-cart-status strong {
      display: block;
    }

    .scanner-cart-status span {
      color: rgba(255,255,255,0.5);
      font-size: 10px;
    }

    .scanner-cart-status strong {
      color: white;
      margin-top: 3px;
      font-size: 16px;
    }

    /*
     * IMPORTANT:
     *
     * There is intentionally NO "Done Scanning"
     * button here.
     *
     * The X button closes the scanner.
     */

    /* =====================================================
       RESPONSIVE
       ===================================================== */

    @media (max-width: 1100px) {
      .product-list {
        height: 470px;
      }

      .cart-list {
        height: 400px;
      }
    }

    @media (max-width: 991px) {
      .product-list {
        height: 390px;
      }

      .cart-list {
        height: 420px;
      }
    }

    @media (max-width: 767px) {
      .pos-page {
        padding: 10px;
      }

      .pos-header {
        padding: 14px;
        border-radius: 16px;
        align-items: flex-start;
        flex-direction: column;
      }

      .pos-header-actions {
        width: 100%;
        justify-content: space-between;
      }

      .pos-scan-button {
        flex: 1;
        justify-content: center;
      }

      .pos-total-header {
        min-width: 125px;
      }

      .product-list {
        height: 360px;
      }

      .cart-list {
        height: auto;
        max-height: 430px;
      }

      .cart-item {
        grid-template-columns: 1fr auto;
      }

      .cart-item-controls {
        grid-column: 2;
        grid-row: 1;
      }

      .cart-item-total {
        grid-column: 1;
        grid-row: 2;
      }

      .remove-item-button {
        grid-column: 2;
        grid-row: 2;
        justify-self: end;
      }

      .scanner-container {
        padding: 12px;
      }

      .scanner-modal .ant-modal {
        max-width: calc(100vw - 16px);
      }

      .scanner-camera-wrapper {
        aspect-ratio: 3 / 4;
        max-height: none;
      }

      .scanner-frame {
        width: min(76vw, 300px);
      }

      .camera-control-bar {
        align-items: stretch;
        flex-direction: column;
      }

      .camera-label {
        font-size: 11px;
      }
    }

    @media (max-width: 480px) {
      .pos-page {
        padding: 7px;
      }

      .pos-header-left {
        width: 100%;
      }

      .pos-header-actions {
        flex-direction: column;
      }

      .pos-scan-button,
      .pos-total-header {
        width: 100%;
      }

      .pos-total-header {
        text-align: center;
      }

      .calculator-screen {
        height: 70px;
      }

      .calculator-screen span {
        font-size: 30px;
      }

      .digit-btn,
      .clear-btn,
      .add-btn {
        height: 50px;
      }

      .scanner-header {
        align-items: flex-start;
      }

      .scanner-header h3 {
        font-size: 20px !important;
      }

      .scanner-camera-wrapper {
        border-radius: 14px;
      }

      .scanner-frame {
        width: 75%;
      }

      .scanner-corner {
        width: 30px;
        height: 30px;
      }

      .scanner-cart-status strong {
        font-size: 14px;
      }
    }
  `;

  // =========================================================
  // RENDER
  // =========================================================

  return (
    <>
      <style>{styles}</style>

      <div className="pos-page">

        {/* ===================================================
            HEADER
        =================================================== */}

        <div className="pos-header">

          <div className="pos-header-left">

            <div className="pos-icon">
              <ShoppingCartOutlined />
            </div>

            <div>
              <Title
                level={3}
                style={{ margin: 0 }}
              >
                Point of Sale
              </Title>

              <div className="pos-header-subtitle">
                Fast checkout • QR product scanning
              </div>
            </div>

          </div>

          <div className="pos-header-actions">

            <Badge
              count={totalItems}
              overflowCount={999}
              showZero
            >
              <button
                className="pos-scan-button"
                onClick={openScanner}
              >
                <ScanOutlined />
                Scan Product
              </button>
            </Badge>

            <div className="pos-total-header">

              <span>
                Current Total
              </span>

              <strong>
                ${formatNumber(overallTotal)}
              </strong>

            </div>

          </div>

        </div>

        {/* ===================================================
            MAIN POS
        =================================================== */}

        <Row
          gutter={[18, 18]}
          className="pos-main"
        >

          {/* =================================================
              PRODUCTS
          ================================================= */}

          <Col xs={24} lg={8}>

            <Card className="pos-panel">

              <div className="panel-title">

                <div>
                  <Title
                    level={4}
                    style={{ margin: 0 }}
                  >
                    Products
                  </Title>

                  <Text type="secondary">
                    Select manually or scan QR
                  </Text>
                </div>

                <Tag color="blue">
                  {products.length}
                </Tag>

              </div>

              <Input
                size="large"
                placeholder="Search products..."
                prefix={<SearchOutlined />}
                value={search}
                onChange={handleSearch}
                allowClear
                className="pos-search"
              />

              <div className="product-list">

                {loading &&
                products.length === 0 ? (

                  <div className="loading-box">
                    <Spin size="large" />
                    <Text>
                      Loading products...
                    </Text>
                  </div>

                ) : filteredProducts.length === 0 ? (

                  <Empty
                    description="No products found"
                  />

                ) : (

                  filteredProducts.map(
                    (product) => (

                      <button
                        key={product.id}
                        type="button"
                        className={`pos-product ${
                          selectedProduct?.id ===
                          product.id
                            ? "selected-product"
                            : ""
                        }`}
                        onClick={() =>
                          handleSelectProduct(product)
                        }
                      >

                        <div className="pos-product-image-wrapper">

                          {product.image ? (

                            <img
                              src={product.image}
                              className="product-image"
                              alt={product.name}
                            />

                          ) : (

                            <div className="product-image-placeholder">
                              <ShoppingCartOutlined />
                            </div>

                          )}

                        </div>

                        <div className="pos-product-details">

                          <Text strong>
                            {product.name}
                          </Text>

                          <Text className="price-text">
                            $
                            {formatNumber(
                              product.price
                            )}
                          </Text>
<Text
  type="secondary"
  className="stock-text"
>
  Stock Available:{" "}
  {getAvailableUnits(product)}
</Text>

                        </div>

                      </button>

                    )
                  )

                )}

              </div>

            </Card>

          </Col>

          {/* =================================================
              CART
          ================================================= */}

          <Col xs={24} lg={10}>

            <Card className="pos-panel">

              <div className="panel-title">

                <div>
                  <Title
                    level={4}
                    style={{ margin: 0 }}
                  >
                    Current Sale
                  </Title>

                  <Text type="secondary">
                    {totalItems} item
                    {totalItems !== 1
                      ? "s"
                      : ""}
                  </Text>
                </div>

                {purchaseItems.length > 0 && (

                  <button
                    className="clear-cart-button"
                    onClick={clearCart}
                  >
                    <DeleteOutlined />
                    {" "}
                    Clear
                  </button>

                )}

              </div>

              <div className="cart-list">

                {purchaseItems.length === 0 ? (

                  <div className="empty-cart">

                    <div className="empty-cart-icon">
                      <ShoppingCartOutlined />
                    </div>

                    <Title level={4}>
                      Cart is empty
                    </Title>

                    <Text type="secondary">
                      Scan a product QR code
                      or select a product.
                    </Text>

                    <button
                      className="empty-scan-button"
                      onClick={openScanner}
                    >
                      <QrcodeOutlined />
                      {" "}
                      Start Scanning
                    </button>

                  </div>

                ) : (

                  purchaseItems.map(
                    (item) => (

                      <div
                        key={item.product}
                        className="cart-item"
                      >

                        <div>
                          <div className="cart-item-name">
                            {item.product_name}
                          </div>

<div className="cart-item-price">
  $
  {formatNumber(item.price)}
  {" × "}
  {item.quantity_taken} unit
  {Number(item.quantity_taken) !== 1 ? "s" : ""}
</div>
                        </div>

                        <div className="cart-item-controls">

                          <button
                            onClick={() =>
                              decreaseCartItem(
                                item
                              )
                            }
                          >
                            <MinusOutlined />
                          </button>

                          <span>
                            {item.quantity_taken}
                          </span>

                          <button
                            onClick={() =>
                              increaseCartItem(
                                item
                              )
                            }
                          >
                            <PlusOutlined />
                          </button>

                        </div>

                        <div className="cart-item-total">
                          $
                          {formatNumber(
                            item.total
                          )}
                        </div>

                        <button
                          className="remove-item-button"
                          onClick={() =>
                            removeCartItem(
                              item
                            )
                          }
                        >
                          <DeleteOutlined />
                        </button>

                      </div>

                    )
                  )

                )}

              </div>

              <Divider />

              <div className="cart-summary">

                <div>
                  <span>
                    Items
                  </span>

                  <strong>
                    {totalItems}
                  </strong>
                </div>

                <div>
                  <span>
                    Products
                  </span>

                  <strong>
                    {purchaseItems.length}
                  </strong>
                </div>

                <div className="grand-total">

                  <span>
                    Total
                  </span>

                  <strong>
                    $
                    {formatNumber(
                      overallTotal
                    )}
                  </strong>

                </div>

              </div>

            </Card>

          </Col>

          {/* =================================================
              KEYPAD
          ================================================= */}

          <Col xs={24} lg={6}>

            <Card className="pos-panel">

              <div className="panel-title">

                <div>

                  <Title
                    level={4}
                    style={{ margin: 0 }}
                  >
                    Quick Add
                  </Title>

                  <Text type="secondary">
                    {selectedProduct
                      ? selectedProduct.name
                      : "Select a product"}
                  </Text>

                </div>

              </div>

              <div className="calculator-screen">
                <span>
                  {quantity || "0"}
                </span>
              </div>

              {selectedProduct && (

                <div className="selected-product-summary">

                  <div>

                    <strong>
                      {selectedProduct.name}
                    </strong>

                    <span>
                      $
                      {formatNumber(
                        selectedProduct.price
                      )}
                    </span>

                  </div>

<small>
  Available Units:{" "}
  {getAvailableUnits(selectedProduct)}
</small>

                </div>

              )}

              <div className="digit-grid">

                {digits.map((digit) => (

                  <button
                    key={digit}
                    className="digit-btn"
                    onClick={() =>
                      handleDigitClick(
                        String(digit)
                      )
                    }
                  >
                    {digit}
                  </button>

                ))}

                <button
                  className="clear-btn"
                  onClick={handleClear}
                >
                  C
                </button>

                <button
                  className="digit-btn"
                  onClick={() =>
                    handleDigitClick("0")
                  }
                >
                  0
                </button>

                <button
                  className="add-btn"
                  onClick={handleAddItem}
                >
                  <PlusOutlined />
                </button>

              </div>

              <button
                className="full-submit-btn"
                onClick={openPaymentModal}
                disabled={!purchaseItems.length}
              >
                <ArrowRightOutlined />
                Checkout
              </button>

            </Card>

          </Col>

        </Row>

        {/* ===================================================
            PAYMENT MODAL
        =================================================== */}

        <Modal
          open={paymentModalOpen}
          footer={null}
          centered
          width={520}
          closable={!loading}
          onCancel={() => {
            if (!loading) {
              setPaymentModalOpen(false);
            }
          }}
          className="payment-modal"
        >

          <div>

            <div className="checkout-heading">

              <div className="checkout-icon">
                <CreditCardOutlined />
              </div>

              <div>

                <Title
                  level={3}
                  style={{ margin: 0 }}
                >
                  Complete Payment
                </Title>

                <Text type="secondary">
                  Select customer payment method
                </Text>

              </div>

            </div>

            <div className="checkout-total">

              <span>
                TOTAL DUE
              </span>

              <strong>
                $
                {formatNumber(
                  overallTotal
                )}
              </strong>

            </div>

            <div className="payment-buttons">

              {/* CASH */}

              <button
                className={`cash-payment-btn ${
                  paymentMethod === "cash"
                    ? "active-payment"
                    : ""
                }`}
                onClick={() => {
                  setPaymentMethod("cash");
                  handleCashPayment();
                }}
                disabled={loading}
              >

                <DollarOutlined />

                <div>

                  <strong>
                    Cash
                  </strong>

                  <small>
                    Pay with cash
                  </small>

                </div>

              </button>

              {/* CARD */}

              <button
                className={`card-payment-btn ${
                  paymentMethod === "card"
                    ? "active-payment"
                    : ""
                }`}
                onClick={() =>
                  setPaymentMethod("card")
                }
                disabled={loading}
              >

                <CreditCardOutlined />

                <div>

                  <strong>
                    Card
                  </strong>

                  <small>
                    Pay using customer PIN
                  </small>

                </div>

              </button>

              {/* ROYALTY */}

              <button
                className="royalty-payment-btn"
                onClick={
                  openRoyaltyPayment
                }
                disabled={loading}
              >

                <CreditCardOutlined />

                <div>

                  <strong>
                    Royalty Card
                  </strong>

                  <small>
                    Use customer loyalty balance
                  </small>

                </div>

              </button>

            </div>

            {paymentMethod === "card" && (

              <div className="card-payment-section">

                <Divider />

                <Text strong>
                  Customer Card PIN
                </Text>

                <Input.Password
                  size="large"
                  placeholder="Enter Card PIN"
                  value={pin}
                  onChange={(event) =>
                    setPin(
                      event.target.value
                    )
                  }
                  prefix={
                    <LockOutlined />
                  }
                  className="card-input"
                  maxLength={20}
                />

                <div className="purchase-total-box">

                  <span>
                    Purchase Total
                  </span>

                  <strong>
                    $
                    {formatNumber(
                      overallTotal
                    )}
                  </strong>

                </div>

                <button
                  className="complete-payment-btn"
                  onClick={
                    completeCardPayment
                  }
                  disabled={
                    cardSubmitting
                  }
                >

                  {cardSubmitting ? (
                    <>
                      <Spin
                        size="small"
                        style={{
                          marginRight: 8,
                        }}
                      />

                      Processing...
                    </>
                  ) : (
                    <>
                      <CreditCardOutlined />
                      {" "}
                      Complete Card Payment
                    </>
                  )}

                </button>

              </div>

            )}

          </div>

        </Modal>

        {/* ===================================================
            AUTOMATIC QR SCANNER
        =================================================== */}

        <Modal
          open={scannerOpen}
          footer={null}
          centered
          width={540}
          destroyOnClose={false}
          closable={false}
          maskClosable={false}
          onCancel={closeScanner}
          className="scanner-modal"
        >

          <div className="scanner-container">

            {/* SCANNER HEADER */}

            <div className="scanner-header">

<div
  style={{
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "center",
    width: "100%",
    gap: "4px",
  }}
>
  <Title
    level={3}
    style={{
      margin: 0,
      lineHeight: 1.2,
      fontWeight: 700,
      color: "#111827",
    }}
  >
    Product Scanner -{" "}
    <span
      style={{
        color: "#f97316",
        fontWeight: 800,
        whiteSpace: "nowrap",
      }}
    >
      ZULUWHISKEY
    </span>
  </Title>

  <div
    style={{
      color: "#6b7280",
      fontSize: "14px",
      fontWeight: 500,
      lineHeight: 1.4,
    }}
  >
    Automatic real-time QR scanning
  </div>
</div>

              <button
                className="scanner-close-button"
                onClick={closeScanner}
                aria-label="Close scanner"
              >
                <CloseOutlined />
              </button>

            </div>

            {/* CAMERA SELECTOR */}

            <div className="camera-control-bar">

              <CameraOutlined
                style={{
                  color: "#22d3ee",
                  fontSize: 18,
                }}
              />

              <span className="camera-label">
                Camera
              </span>

              <Select
                className="scanner-camera-select"
                value={
                  selectedCameraId ||
                  undefined
                }
                placeholder="Select camera"
                options={cameraOptions}
                onChange={
                  handleCameraChange
                }
                loading={
                  scannerLoading ||
                  cameraSwitching
                }
                disabled={
                  scannerLoading ||
                  cameraSwitching ||
                  !cameraDevices.length
                }
                dropdownStyle={{
                  zIndex: 99999,
                }}
              />

            </div>

            {/* CAMERA */}

            <div className="scanner-camera-wrapper">

              <video
                ref={videoRef}
                className="scanner-video"
                muted
                playsInline
                autoPlay
              />

              {/* QR TARGET */}

              <div className="scanner-frame">

                <div className="scanner-corner top-left" />
                <div className="scanner-corner top-right" />
                <div className="scanner-corner bottom-left" />
                <div className="scanner-corner bottom-right" />

                {/* MOVING LASER */}

                <div className="scanner-line" />

              </div>

              {/* CAMERA LOADING */}

              {scannerLoading && (

                <div className="scanner-loading">

                  <Spin size="large" />

                  <span>
                    Starting camera...
                  </span>

                </div>

              )}

            </div>

            {/* ERROR */}

            {scannerError && (

              <div className="scanner-error-box">

                <div>
                  {scannerError}
                </div>

                <button
                  className="scanner-retry"
                  onClick={() =>
                    startScanner(
                      selectedCameraId
                    )
                  }
                >
                  <ReloadOutlined />
                  {" "}
                  Try Again
                </button>

              </div>

            )}

            {/* LIVE STATUS */}

            <div
              className={`scanner-status ${
                scanStatus === "success"
                  ? "success"
                  : scanStatus === "error"
                  ? "error"
                  : ""
              }`}
            >

              {scannerStatus[
                scanStatus
              ]?.icon}

              {scannerStatus[
                scanStatus
              ]?.label}

            </div>

            {/* LAST PRODUCT */}

            {lastScannedProduct && (
              <div className="last-scanned-product">

                <div className="last-scanned-product-icon">
                  <CheckCircleOutlined />
                </div>

                <div>

                  <strong>
                    {lastScannedProduct.name}
                  </strong>

                  <span>
                    Added to cart • Scanner remains active
                  </span>

                </div>

              </div>
            )}

            {/* INSTRUCTIONS */}

            <div className="scanner-instructions">

              <QrcodeOutlined />

              <div>

                <strong>
                  Automatic Scanning Active
                </strong>

                <span>
                  Point the QR code inside the
                  illuminated frame. The scanner
                  detects and verifies it automatically.
                  No scan button is required.
                </span>

              </div>

            </div>

            {/* CART STATUS */}

            <div className="scanner-cart-status">

              <div>

                <span>
                  CART ITEMS
                </span>

                <strong>
                  {totalItems}
                </strong>

              </div>

              <div>

                <span>
                  CURRENT TOTAL
                </span>

                <strong>
                  $
                  {formatNumber(
                    overallTotal
                  )}
                </strong>

              </div>

            </div>

          </div>

        </Modal>

        {/* ===================================================
            RECEIPT
        =================================================== */}

        <PurchaseReceipt
          open={receiptOpen}
          onClose={() =>
            setReceiptOpen(false)
          }
          receiptData={receiptData}
          receiptTotal={receiptTotal}
          formatNumber={formatNumber}
        />

        {/* ===================================================
            ROYALTY PAYMENT
        =================================================== */}

        <RoyaltyCardPayment
          open={royaltyOpen}
          onClose={() =>
            setRoyaltyOpen(false)
          }
          purchaseItems={purchaseItems}
          total={overallTotal}
          selectedYear={selectedYear}
          customer={selectedCustomer}
          formatNumber={formatNumber}
          resetCart={() => {
            setPurchaseItems([]);
            setQuantity("");
            setSelectedProduct(null);
            setRoyaltyOpen(false);
            setPaymentModalOpen(false);
          }}
        />

      </div>
    </>
  );
};

export default Purchase;

